import jQuery from "jquery";
import "lightcase/src/css/lightcase.css";
import "./lightcaseGallery.css";
import { getAccessToken } from "../../../../utils/APIs/commonHeadApiLogic";

// lightcase.js calls })(jQuery) at load time — jQuery must be global first.
// Static `import "lightcase"` is hoisted above this, so use require after assignment.
window.jQuery = window.$ = jQuery;

require("lightcase");
// Skip lightcase's jQuery Mobile touch bundle — it expects browser `this` (window)
// and crashes in webpack. Swipe is optional; prev/next + arrow keys still work.

const BATCH_SIZE = 500;
let activeLightcaseVideoSrc = "";

const VIDEO_MIME_BY_EXT = {
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  wmv: "video/x-ms-wmv",
  flv: "video/x-flv",
  ogv: "video/ogg",
  ogg: "video/ogg",
};

export const isVideoMediaKey = (key) =>
  /\.(mp4|mov|webm|mkv|avi|wmv|flv|ogv|ogg)$/i.test(key || "");

export const getVideoMimeType = (keyOrUrl = "") => {
  const match = String(keyOrUrl).toLowerCase().match(/\.([a-z0-9]+)(?:\?|#|$)/);
  const ext = match?.[1];
  return ext ? VIDEO_MIME_BY_EXT[ext] || "" : "";
};

export const getVideoPlaybackUrl = (bookingId, item) => {
  const fallbackUrl = item?.url || "";
  const key = item?.key || "";

  if (/^https?:\/\//i.test(fallbackUrl)) return fallbackUrl;
  if (!bookingId || !key) return fallbackUrl;

  const token = getAccessToken();
  const baseUrl = (process.env.REACT_APP_PHOTOGRAPHY_ADMIN_BASE_API_URL || "").trim();
  if (!token || !baseUrl) return fallbackUrl;

  const encodedKey = key
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  return `${baseUrl}/api/admins/stream/${bookingId}/${encodedKey}?token=${encodeURIComponent(token)}`;
};

export const resolveGalleryMediaUrl = (bookingId, item) => {
  if (isVideoMediaKey(item?.key)) {
    return getVideoPlaybackUrl(bookingId, item);
  }
  return item?.url || "";
};

export const getLightcaseOptionsForLink = (link, baseOptions = LIGHTCASE_OPTIONS) => {
  if (!link) return baseOptions;

  const mediaType = link.getAttribute("data-media-type");
  if (mediaType === "video") {
    return {
      ...baseOptions,
      type: "video",
      href: link.getAttribute("data-video-src") || link.getAttribute("href") || link.href,
    };
  }

  const lcOptions = link.getAttribute("data-lc-options");
  if (lcOptions) {
    try {
      return { ...baseOptions, ...JSON.parse(lcOptions) };
    } catch {
      return baseOptions;
    }
  }

  return baseOptions;
};

const prepareLightcaseVideo = (originLink) => {
  const video = document.querySelector("#lightcase-content video");
  if (!video) return;

  const src =
    activeLightcaseVideoSrc ||
    originLink?.getAttribute?.("data-video-src") ||
    originLink?.getAttribute?.("href") ||
    originLink?.href ||
    video.getAttribute("src") ||
    "";

  video.controls = true;
  video.playsInline = true;
  video.preload = "metadata";
  video.autoplay = true;
  video.setAttribute("autoplay", "true");

  if (!src) return;

  video.querySelectorAll("source").forEach((node) => node.remove());

  if (video.getAttribute("src") !== src) {
    video.setAttribute("src", src);
  }

  video.load();
  video.play?.().catch(() => {
    video.muted = true;
    video.play?.().catch(() => {});
  });
};

const setActiveLightcaseVideoSrc = (link) => {
  activeLightcaseVideoSrc =
    link?.getAttribute?.("data-video-src") ||
    link?.getAttribute?.("href") ||
    link?.href ||
    "";
};

export const LIGHTCASE_OPTIONS = {
  transition: "fade",
  transitionOpen: "fade",
  transitionClose: "fade",
  speedIn: 250,
  speedOut: 200,
  showSequenceInfo: true,
  showTitle: true,
  showCaption: false,
  swipe: true,
  useKeys: true,
  navigateEndless: false,
  maxWidth: 1200,
  maxHeight: 800,
  labels: {
    'sequenceInfo.of': ' / '
  },
  typeMapping: {
    image: "jpg,jpeg,gif,png,bmp,webp",
    flash: "swf",
    video: "mp4,mov,ogv,ogg,webm,mkv,avi,wmv,flv",
    iframe: "html,php",
    ajax: "json,txt",
    inline: "#",
  },
  video: {
    width: 960,
    height: 540,
    controls: true,
    autoplay: true,
    preload: "metadata",
    autobuffer: true,
    loop: false,
  },
  onFinish: {
    prepareVideo() {
      if (document.documentElement.getAttribute("data-lc-type") === "video") {
        prepareLightcaseVideo(this);
      }
    },
    updateNavArrows() {
      const seqText = document.querySelector("#lightcase-sequenceInfo")?.textContent || "";
      const prevBtn = document.querySelector("a.lightcase-icon-prev");
      const nextBtn = document.querySelector("a.lightcase-icon-next");

      if (seqText && prevBtn && nextBtn) {
        const match = seqText.match(/(\d+)\s*\/\s*(\d+)/);
        if (match) {
          const current = parseInt(match[1], 10);
          const total = parseInt(match[2], 10);

          if (current === 1) {
            prevBtn.classList.add("lc-disabled");
          } else {
            prevBtn.classList.remove("lc-disabled");
          }

          if (current === total) {
            nextBtn.classList.add("lc-disabled");
          } else {
            nextBtn.classList.remove("lc-disabled");
          }
        }
      }
    },
  },
  onCleanup: {
    resetNavButtons() {
      window.jQuery?.(
        "a.lightcase-icon-prev, a.lightcase-icon-next, a.lightcase-icon-close"
      ).hide().css({ opacity: "", visibility: "" }).removeClass("lc-disabled");

      document.querySelector("#lightcase-content video")?.pause?.();
      activeLightcaseVideoSrc = "";
    },
  },
};

/**
 * Append lightcase anchor links in batches so large galleries (10K+) stay responsive.
 */
export function appendLightcasePoolLinks(
  container,
  items,
  groupName,
  startIndex = 0,
  bookingId = ""
) {
  return new Promise((resolve) => {
    if (!container || !items?.length) {
      resolve();
      return;
    }

    let index = startIndex;

    const processBatch = () => {
      const fragment = document.createDocumentFragment();
      const end = Math.min(index + BATCH_SIZE, items.length);

      for (; index < end; index += 1) {
        const item = items[index];
        if (!item?.url) continue;

        const playbackUrl = resolveGalleryMediaUrl(bookingId, item);
        const link = document.createElement("a");
        link.href = playbackUrl;
        link.id = `lc-gallery-${item.id}`;
        link.setAttribute("data-rel", `lightcase:${groupName}`);
        link.setAttribute("data-lc-index", String(index));
        link.setAttribute("aria-hidden", "true");
        link.tabIndex = -1;

        let filename = "";
        if (item.key) {
          filename = item.key.split("/").pop() || "";
        } else if (item.url) {
          filename = item.url.split("/").pop().split("?")[0] || "";
        }
        try {
          filename = decodeURIComponent(filename);
        } catch {
          // fallback
        }
        if (filename) {
          link.setAttribute("title", filename);
        }

        if (isVideoMediaKey(item.key)) {
          link.setAttribute("data-media-type", "video");
          link.setAttribute("data-file-key", item.key);
          link.setAttribute("data-video-src", playbackUrl);
          if (bookingId) {
            link.setAttribute("data-booking-id", bookingId);
          }
          link.setAttribute(
            "data-lc-options",
            JSON.stringify({ type: "video" })
          );
        }

     

        fragment.appendChild(link);
      }

      if (fragment.childNodes.length) {
        container.appendChild(fragment);
      }

      if (index < items.length) {
        requestAnimationFrame(processBatch);
      } else {
        resolve();
      }
    };

    requestAnimationFrame(processBatch);
  });
}

export function clearLightcasePool(container) {
  if (container) {
    container.innerHTML = "";
  }
}

export function bindLightcaseLinks(container, options = LIGHTCASE_OPTIONS) {
  if (!container || !window.jQuery?.fn?.lightcase) return;

  window
    .jQuery(container)
    .find('a[data-rel^="lightcase"]:not([data-lightcase-bound])')
    .each(function bindLink() {
      const $link = window.jQuery(this);

      $link.attr("data-lightcase-bound", "1");
      $link.off("click.lightcase").on("click.lightcase", function onClick(event) {
        event.preventDefault();
        const fileKey = $link.attr("data-file-key");
        const linkBookingId = $link.attr("data-booking-id");
        if (fileKey && linkBookingId && isVideoMediaKey(fileKey)) {
          const playbackUrl = getVideoPlaybackUrl(linkBookingId, {
            key: fileKey,
            url: $link.attr("data-video-src") || $link.attr("href"),
          });
          $link.attr(
            "href",
            playbackUrl
          );
          $link.attr("data-video-src", playbackUrl);
          setActiveLightcaseVideoSrc(this);
        }
        $link.lightcase("start", getLightcaseOptionsForLink(this, options));
      });
    });
}

export function openLightcaseItem(
  itemId,
  options = LIGHTCASE_OPTIONS,
  bookingId = "",
  item = null
) {
  const link = document.getElementById(`lc-gallery-${itemId}`);
  if (!link || !window.jQuery?.fn?.lightcase) return false;

  if (item && bookingId && isVideoMediaKey(item.key)) {
    const playbackUrl = getVideoPlaybackUrl(bookingId, item);
    link.href = playbackUrl;
    link.setAttribute("data-video-src", playbackUrl);
    setActiveLightcaseVideoSrc(link);
  } else {
    activeLightcaseVideoSrc = "";
  }

  if (item) {
    let filename = "";
    if (item.key) {
      filename = item.key.split("/").pop() || "";
    } else if (item.url) {
      filename = item.url.split("/").pop().split("?")[0] || "";
    }
    try {
      filename = decodeURIComponent(filename);
    } catch {
      // fallback
    }
    if (filename) {
      link.setAttribute("title", filename);
    }
  }

  const linkOptions = getLightcaseOptionsForLink(link, options);
  window.jQuery(link).lightcase("start", linkOptions);
  return true;
}

export function destroyLightcaseBindings() {
  if (!window.jQuery?.fn?.lightcase) return;
  window.jQuery('a[data-rel^="lightcase"]').off("click.lightcase");
}
